import { useState, useEffect } from "react";
import {
  IconPencil,
  IconPlus,
  IconSpinner,
  IconTrash,
} from "../icons";
import { notifyError, notifySuccess } from "../notify";
import { useAdminModal } from "../components/AdminModalProvider";
import AdminModal from "../components/AdminModal";

export default function AddonsManagement() {
  const { confirm } = useAdminModal();
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [formData, setFormData] = useState({
    addonId: "",
    title: "",
    description: "",
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAddons();
  }, []);

  const fetchAddons = async () => {
    try {
      const response = await fetch("/api/addons", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch addons");
      }
      const data = await response.json();
      setAddons(data);
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openModal = (addon = null) => {
    if (addon) {
      setFormData({
        addonId: addon.addonId,
        title: addon.title,
        description: addon.description,
      });
      setEditingAddon(addon);
    } else {
      setFormData({ addonId: "", title: "", description: "" });
      setEditingAddon(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (actionLoading) return;
    setIsModalOpen(false);
    setFormData({ addonId: "", title: "", description: "" });
    setEditingAddon(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.addonId.trim() || !formData.title.trim() || !formData.description.trim()) {
      notifyError("All fields are required");
      return;
    }

    setActionLoading(true);
    try {
      const url = editingAddon
        ? `/api/addons/${editingAddon.id}`
        : "/api/addons";
      const method = editingAddon ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          addonId: formData.addonId.trim(),
          title: formData.title.trim(),
          description: formData.description.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save addon");
      }

      await fetchAddons();
      closeModal();
      notifySuccess(
        `Addon successfully ${editingAddon ? "updated" : "created"}`
      );
    } catch (error) {
      notifyError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    const confirmed = await confirm({
      title: "Delete addon?",
      description: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "danger",
    });

    if (!confirmed) return;

    setActionLoading(true);
    try {
      const response = await fetch(`/api/addons/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete addon");
      }

      await fetchAddons();
      notifySuccess(`Addon "${title}" was successfully deleted.`);
    } catch (error) {
      notifyError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <IconSpinner size={32} />
        <p>Loading addons...</p>
      </div>
    );
  }

  return (
    <div className="addons-container category-management">
      <div className="category-management__toolbar">
        <button
          type="button"
          className="category-management__add"
          onClick={() => openModal()}
          disabled={actionLoading}
        >
          <IconPlus size={16} />
          Add New
        </button>
      </div>

      <div className="management-grid">
        {addons.length === 0 ? (
          <div className="no-data-message">
            No addons found. Click Add New to create one.
          </div>
        ) : (
          addons.map((addon) => (
            <div key={addon.id} className="management-card">
              <div className="card-content">
                <h3>{addon.title}</h3>
                <p className="date-info">ID: {addon.addonId}</p>
                <p className="date-info">{addon.description}</p>
              </div>
              <div className="card-actions">
                <button
                  type="button"
                  className="edit-btn"
                  onClick={() => openModal(addon)}
                  disabled={actionLoading}
                  title="Edit addon"
                >
                  <IconPencil size={18} />
                </button>
                <button
                  type="button"
                  className="delete-btn"
                  onClick={() => handleDelete(addon.id, addon.title)}
                  disabled={actionLoading}
                  title="Delete addon"
                >
                  <IconTrash size={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <AdminModal
        open={isModalOpen}
        onClose={closeModal}
        closeOnOverlay={!actionLoading}
        showClose={!actionLoading}
        title={editingAddon ? "Edit addon" : "Add addon"}
        description={
          editingAddon
            ? "Update the addon details below."
            : "Create a new addon for the portal."
        }
        size="lg"
        footer={
          <>
            <button
              type="button"
              className="admin-modal__btn admin-modal__btn--secondary"
              onClick={closeModal}
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="addon-form"
              className="admin-modal__btn admin-modal__btn--primary"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <IconSpinner size={16} />
                  Saving…
                </>
              ) : editingAddon ? (
                "Update"
              ) : (
                "Create"
              )}
            </button>
          </>
        }
      >
        <form id="addon-form" onSubmit={handleSubmit}>
          <div className="admin-modal__field">
            <label htmlFor="addonId">Addon ID</label>
            <input
              type="text"
              id="addonId"
              name="addonId"
              value={formData.addonId}
              onChange={handleInputChange}
              disabled={actionLoading}
              placeholder="Enter addon ID"
              autoFocus
            />
          </div>
          <div className="admin-modal__field">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={actionLoading}
              placeholder="Enter addon title"
            />
          </div>
          <div className="admin-modal__field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={actionLoading}
              placeholder="Enter addon description"
            />
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
